import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IMemberSalesOrderInfoResult, ITourPrice } from '../utils/types';
import {
  getTourPricesOptions,
  updateBedTypeSalesIndividualMaterial,
} from '../api';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { AuthContext } from '../context/AuthContext';

interface ChangeBedTypeDialogProps {
  open: boolean;
  onClose: () => void;
  data?: IMemberSalesOrderInfoResult;
}

export default function ChangeBedTypeDialog({
  open,
  onClose,
  data,
}: ChangeBedTypeDialogProps) {
  const { t } = useTranslation();
  const showSnackbar = useAlertSnackbar();
  const { token } = useContext(AuthContext);

  const [selected, setSelected] = useState<number | ''>('');
  const [tourPriceOptions, setTourPriceOptions] = useState<ITourPrice[]>([]);
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    if (!open || !data?.tourId) return;

    let mounted = true;
    setError(false);
    setLoading(true);
    setTourPriceOptions([]);

    const fetchBedTypes = async () => {
      try {
        const res = await getTourPricesOptions(data.salesIndividualId, token);
        if (!res.ok) throw new Error(res.statusText);

        const json = await res.json().catch(() => []);
        if (mounted) {
          setTourPriceOptions(json);
          if (json.length === 0) {
            setError(true);
          }
        }
      } catch (err) {
        console.error(err);
        if (mounted) {
          showSnackbar(t('orderDetailPage.changeBedType.fetchError'), 'error');
          setError(true);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchBedTypes();

    return () => {
      mounted = false;
    };
  }, [open, data?.tourId]);

  if (!data) return null;

  const handleConfirm = async () => {
    setError(false);
    try {
      const res = await updateBedTypeSalesIndividualMaterial(
        data.salesIndividualId,
        selected,
        'response',
        token,
      );
      if (res.status === 200) {
        showSnackbar(t('orderDetailPage.changeBedType.success'), 'success');
        handleCloseDialog();
      } else {
        const errorBody = await res.json();
        throw new Error(errorBody.detail || res.statusText);
      }
    } catch (err: any) {
      showSnackbar(
        err.message || t('orderDetailPage.changeBedType.error'),
        'error',
      );
      setErrorMessage(err.message);
      setError(true);
    }
  };

  const handleCloseDialog = () => {
    setError(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center' }}>
        {t('orderDetailPage.changeBedType.title')}
      </DialogTitle>

      {new Date(data?.lastRoomChangeDate) < today && (
        <Alert severity="warning" sx={{ mb: 2, cursor: 'pointer' }}>
          {t('orderDetailPage.transferTourDialog.dateWarningText')}
        </Alert>
      )}
      {!(new Date(data?.lastRoomChangeDate) < today) && (
        <DialogContent>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              p={3}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: '#ffe6e6',
                    borderColor: '#f44336',
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography
                      component="span"
                      sx={{ color: '#f44336', fontWeight: 'bold', mr: 1 }}
                    >
                      ⚠️ {t('orderDetailPage.changeBedType.unavailableTitle')}
                    </Typography>
                  </Box>
                  <Typography>{errorMessage}</Typography>
                </Paper>
              )}

              <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}
              >
                <Typography>
                  <strong>
                    {t('orderDetailPage.changeBedType.currentType')}:
                  </strong>{' '}
                  {data.bedType}
                </Typography>
                <Typography>
                  <strong>
                    {t('orderDetailPage.changeBedType.currentPrice')}:
                  </strong>{' '}
                  {data.totalAmount} {data.currency}
                </Typography>
                <Typography>
                  <strong>
                    {t('orderDetailPage.changeBedType.orderNumber')}:
                  </strong>{' '}
                  {data.orderNumber}
                </Typography>
              </Paper>

              {tourPriceOptions.length === 0 && !error && (
                <Typography sx={{ textAlign: 'center', p: 2 }}>
                  {t('orderDetailPage.changeBedType.unavailableTitle')}
                </Typography>
              )}

              {tourPriceOptions.length > 0 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="bed-type-select-label">
                    {t('orderDetailPage.changeBedType.selectLabel')}
                  </InputLabel>
                  <Select
                    labelId="bed-type-select-label"
                    value={selected}
                    label={t('orderDetailPage.changeBedType.selectLabel')}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelected(typeof val === 'string' ? Number(val) : val);
                    }}
                  >
                    {tourPriceOptions.map((opt) => (
                      <MenuItem key={opt.price.id} value={opt.price.id}>
                        {opt.price.bedType.name} – {opt.price.amount}{' '}
                        {opt.price.currency.code}-{opt.price.concept.name}-
                        {opt.price.bedStatus.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}
        </DialogContent>
      )}

      {!loading && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={handleCloseDialog}>
            {t('orderDetailPage.changeBedType.cancel')}
          </Button>
          {(!data?.lastRoomChangeDate ||
            new Date(data.lastRoomChangeDate) >= today) && (
            <Button
              variant="contained"
              onClick={handleConfirm}
              fullWidth
              disabled={selected === '' || loading}
            >
              {t('orderDetailPage.changeBedType.confirm')}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
